<template>
    <div class="itempage">
        <!--
            LOADING
         -->
        <div class="top"></div>
        <transition name="fade">
            <div v-if="loading" class="loading">
                <load-indicator
                :loadText="`Item #${itemId}`"></load-indicator>
            </div>
        </transition>
        <!-- <div class="go-back"
            v-on:click="goBack">
            <i class="fa fa-angle-double-left"></i>Back
        </div> -->
        <!--
            ERROR
         -->
        <transition name="fade">
            <div class="error" v-if="error">
                <div class="go-back"
                    v-on:click="goBack"
                    v-if="!$route.query.embed">
                    <i class="fa fa-angle-double-left"></i>Back
                </div>
                <big-error-box 
                    :errorTitle="'Error: ' + error.statusText" 
                    :errorContent="error.bodyText" 
                    :secondaryInfo="`Item ID #${itemId}`"
                    canRetry="true"
                    v-on:retry="fetchData"
                    iconClass="fa-exclamation-triangle"></big-error-box>
            </div>
        </transition>
        <!--
            RESULT
         -->
        <div v-if="!loading && itemData">
            <!-- <div class="go-back"
                v-on:click="goBack"
                v-if="!$route.query.embed">
                <i class="fa fa-angle-double-left"></i>Back
            </div> -->

            <div class="item-summary">
            <div class="page-header">
                <div class="icon">
                    <item-icon
                        class="centering"
                        :iconIndex="itemData.iconIndex"
                        :rank="itemData.rank"
                        :type="itemData.type.type"
                        :count="itemData.maxStack"
                        :enhanceLvl="enhanceLevel"
                    ></item-icon>
                </div>
                <div class="title">
                    <div class="remark">
                        <span class="iid">#{{ itemId }}<span class="tech-text" v-if="techMode"> · {{ ownerTable }}</span></span>
                    </div>
                    <h2 class="head">
                        <span class="enhance" v-if="enhanceLevel > 0">+{{ enhanceLevel }} </span>
                        <ui-string
                            :mid="nameMessage.id"
                            :alt="nameMessage.message"
                            :params="itemData.name._NameIDParam"
                            :inline="true"
                        />
                    </h2>
                    <div class="chips">
                        <span class="dv-chip" v-if="itemData.level > 1">Lv {{ itemData.level }}</span>
                        <span class="dv-chip" v-if="itemData.tier" v-html="itemData.tier"></span>
                        <span class="dv-chip" :class="'rank-' + itemData.rank.toLowerCase()">{{ itemData.rank }}</span>
                        <span class="dv-chip" v-if="canUse">{{ canUse }}<span class="tech-note" v-if="techMode && canUseIds"> ({{ canUseIds }})</span></span>
                        <span class="dv-chip" v-if="itemData.cashItem">Cash</span>
                        <span class="dv-chip">{{ category }}<span class="tech-note" v-if="techMode"> {{ typeTechNote }}</span></span>
                    </div>
                </div>
                <div class="actions">
                    <copy-link :copyContent="link">
                        <i class="fa fa-share-square-o"></i> Copy link
                        <template slot="ok"><i class="fa fa-check"></i> Link copied!</template>
                    </copy-link>
                </div>
                <div class="favorite">
                    <span class="helper">
                        {{ favorite ? 'Remove favorite' : 'Add favorite' }}
                    </span>
                    <i class="fa fav-star" :class="favorite ? 'fa-star favorited' : 'fa-star-o'" @click="toggleFavorite"/>
                </div>
            </div>

            <item-attribs
                :itemData="itemData"
                :forceBound="forceBound"
                :combinedGearScore="combinedGearScore"
                :effectiveEnhanceLevel="effectiveEnhanceLevel"/>
            </div><!-- /item-summary -->

            <div class="item-body">
            <div class="stat-desc-row" :class="{ 'no-stats': !(itemData.stats.length || (hasEnhance && !enhanceError)) }">
                    <div class="stats-panel" v-if="itemData.stats.length || (hasEnhance && !enhanceError)">
                        <div class="enhance-control" v-if="hasEnhance && !enhanceError">
                            <div class="enhance-opts">
                                <div class="checkbox" v-if="!$fOn($f.DisableGoldenGoose)">
                                    <input type="checkbox" id="enhGoldenGoose" v-model="useGoldenGoose"/>
                                    <label for="enhGoldenGoose">Golden goose</label>
                                </div>
                                <div class="checkbox" v-if="canUseJelly">
                                    <input type="checkbox" id="enhJelly" v-model="useJelly"/>
                                    <label for="enhJelly">Protection jelly</label>
                                </div>
                            </div>
                            <rank-slider
                                :value="enhanceLevel"
                                :min="0"
                                :max="maxEnhanceLevel"
                                :dangerStart="breakThreshold || Number.MAX_SAFE_INTEGER"
                                label="Enhancement level"
                                :disabled="enhanceLoading"
                                v-on:input="onLevelUpdate"
                                v-on:step="onEnhanceStep"/>
                            <div class="rank-caption">
                                <b>+{{ enhanceLevel }}</b><span v-if="maxEnhanceLevel"> / +{{ maxEnhanceLevel }}</span><span
                                    class="breaks" v-if="breakThreshold"> · breaks past +{{ breakThreshold - 1 }}</span>
                            </div>
                        </div>

                        <stat-grid v-if="itemData.stats.length"
                            :statSet="statSet"
                            :enhanceStatSet="enhanceLevelStats"></stat-grid>

                        <div class="vehicle-speed"
                            v-if="itemType == 'VEHICLE_MULTI_MOUNT' || itemType == 'VEHICLE'">
                            Vehicle movement speed {{ itemData.type.movementSpeedPercent }}%
                        </div>
                    </div>

                    <div class="description-panel">
                        <div class="desc">
                            <div v-if="itemData.desc">
                                <ui-string
                                    :mid="itemData.desc._DescID"
                                    :alt="itemData.desc.desc"
                                    :params="itemData.desc._DescIDParam"
                                    format="html"
                                />
                            </div>
                            <!-- <div v-if="itemData.desc" v-html="itemData.desc.desc" class="uistring"></div> -->
                            <div v-else>No description</div>
                            <div v-if="itemData.gainText" class="game-tooltip">
                                <div v-html="itemData.gainText" class="gain uistring"></div>
                            </div>

                            <div class="skill" v-if="itemData.skillInfo">
                                <skill-stub-link :skill-id="itemData.skillInfo.id" />
                            </div>

                            <div class="gems" v-if="itemData.gemslots">
                                <div
                                    class="skillgem gemslot tooltip"
                                    v-if="itemData.gemslots.skill"
                                    :style="`background: url('${ddsPngUrl('uit_re_itemjewelslot03')}') 1px 0, url('${ddsPngUrl('uit_itemslot_re01_d')}') -4px -4px;`">
                                    <div class="count" v-if="itemData.gemslots.skill > 1">
                                        x{{itemData.gemslots.skill}}
                                    </div>
                                    <div class="tooltext">
                                        <div class="content">Skill gem</div>
                                    </div>
                                </div>
                                <div
                                    class="offensive gemslot tooltip"
                                    v-if="itemData.gemslots.offensive"
                                    :style="`background: url('${ddsPngUrl('uit_re_itemjewelslot02')}') 1px 0, url('${ddsPngUrl('uit_itemslot_re01_d')}') -4px -4px;`">
                                    <div class="count" v-if="itemData.gemslots.offensive > 1">
                                        x{{itemData.gemslots.offensive}}
                                    </div>
                                    <div class="tooltext">
                                        <div class="content">Offensive gem</div>
                                    </div>
                                </div>
                                <div
                                    class="defensive gemslot tooltip"
                                    v-if="itemData.gemslots.defensive"
                                    :style="`background: url('${ddsPngUrl('uit_re_itemjewelslot01')}') 1px 0, url('${ddsPngUrl('uit_itemslot_re01_d')}') -4px -4px;`">
                                    <div class="count" v-if="itemData.gemslots.defensive > 1">
                                        x{{itemData.gemslots.defensive}}
                                    </div>
                                    <div class="tooltext">
                                        <div class="content">Defensive gem</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="extra-data">
                            <div class="color"
                                v-if="itemType == 'HAIRDYE' || itemType == 'CONTACT_LENS' || itemType == 'TWO_TONE_HAIRDYE'">
                                <item-custom-color :colorCode="itemData.type.color" />
                            </div>
                            <div class=""
                                v-if="itemType == 'SKILL_RESET_SCROLL' || itemType == 'UNLIMITED_RESET_SCROLL'">
                                Resets your <strong>{{ itemData.type.minSpec | ordinal}}
                                <span v-if="itemData.type.minSpec != itemData.type.maxSpec">
                                    through {{ itemData.type.maxSpec | ordinal }}
                                </span>
                                </strong>
                                specialization's skills.
                            </div>
                            <div class=""
                                v-if="itemType == 'REVIVAL_SCROLL'">
                                Adds <strong>{{ itemData.type.reviveIncrAmt }} cash revive(s)</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'PET_ALICORN'">
                                Revives an ally for <strong>{{ itemData.type.reviveDurationDays }} day(s).</strong>
                            </div>
                            <div class=""
                                v-if="itemType == 'DNP_COUPON'">
                                Credits <strong>{{ itemData.type.dnpAmount | thousands }} DNP</strong> to your account.
                            </div>
                            <div class=""
                                v-if="itemType == 'EXP_SCROLL'">
                                Increases EXP gained by <strong>{{ itemData.type.expBoostPercent }}%</strong>
                                for characters up to <strong>Level {{ itemData.type.maxLevel }}</strong>
                            </div>
                            <div class=""
                                v-if="itemType == 'PET_EXP_JUICE'">
                                Increases ally's EXP by <strong>{{ itemData.type.expAmt | thousands }}</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'INVENTORY_SLOT_EXPANSION'">
                                Expands your inventory by <strong>{{ itemData.type.slotIncrAmt | thousands }} slot(s)</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'STORAGE_SLOT_EXPANSION'">
                                Expands your storage by <strong>{{ itemData.type.slotIncrAmt | thousands }} slot(s)</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'EXP_UP_ITEM'">
                                Increases your EXP by <strong>{{ itemData.type.expIncrAmt | thousands }}</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'ULTIMATE_FTG_UP'">
                                Can only be used by characters <strong>level {{ itemData.type.levelCap }} and below</strong>.
                                <div v-if="itemData.type.isUnlimited"><strong>Unlimited</strong> uses (not consumed when used).</div>
                                <div v-else><strong>One-time</strong> use.</div>
                            </div>
                            <div class=""
                                v-if="itemType == 'PVP_EXP_UP'">
                                Increases your Arena EXP by <strong>{{ itemData.type.expIncrAmt | thousands }}</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'NEW_USER_EXP_INCREASE'">
                                Increases your EXP by <strong>{{ itemData.type.expIncrAmtPercent }}%</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'TALISMAN_SLOT_EXPANSION'">
                                EC Talisman slots last for <strong>{{ itemData.type.durationDays }} day(s)</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'MERCENARY_EXP_POTION'">
                                Increases your active mercenary's EXP by <strong>{{ itemData.type.expBoostAmt | thousands}}</strong>.<br/>
                                Can be used on mercenaries <strong>below Level {{ itemData.type.maxMercLevel }}</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'BONOUS_FISHING_SEA_FISH'">
                                Worth <strong>{{ itemData.type.weight }} fishing point(s)</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'UNSEAL_INCR_AND_WARRANTY'">
                                Usable on
                                <strong>
                                    <span v-if="itemData.type.isCash">cash items</span>
                                    <span v-else>unbindable items</span>
                                </strong>
                                only.
                            </div>
                            <div class=""
                                v-if="itemType == 'JUMP_TO_LEVEL'">
                                Jumps your character to <strong>Level {{ itemData.type.targetLevel }}</strong>.<br/>
                                Cannot be used if over <strong>Level {{ itemData.type.targetLevel }}</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'MERC_EXPLORATION_AWAKENING_POTION'">
                                Can only use <strong>{{ itemData.type.maxSimulUse }}</strong> at a time.
                            </div>
                            <div class=""
                                v-if="itemType == 'HERO_LEVEL_EXP_POTION'">
                                Increases your Hero EXP by <strong>{{ itemData.type.heroExpIncrAmt }}</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'HERO_LEVEL_JUMP_TO_LEVEL'">
                                Jumps your Hero EXP to <strong>Hero Level {{ itemData.type.targetLevel }}</strong>.<br/>
                                Cannot be used if at or over <strong>Hero Level {{ itemData.type.targetLevel }}</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'HERO_LEVEL_EXP_SCROLL'">
                                Increases your Hero EXP gain by <strong>{{ itemData.type.heroExpIncrAmtPercent }}%</strong>.
                            </div>
                            <div class=""
                                v-if="itemType == 'FORTUNE_COIN_POINTS'">
                                <!-- TODO -->
                                Gain <point-tag :pointId="itemData.type.coinType" :amount="itemData.type.amount"></point-tag>
                                upon acquisition or activation.
                            </div>
                            <div class=""
                                v-if="itemType == 'TITLE'">
                                Grants the following title when used:
                                <title-info :titleId="itemData.type.appellationId"/>
                            </div>
                        </div>
                    </div>
            </div>

            <!--
                Decal parts only: the 3D viewer that used to fill this box for
                skn parts is long disabled, so for those it could only ever be
                an empty well.
            -->
            <div class="section page-section model-section" v-if="hasDecal && !$route.query.embed">
                <item-parts
                    :itemId="itemData.id"
                    :parts="itemData.parts"
                    :type="itemData.type"
                    :key="`parts-${itemData.id}`"/>
            </div>

            <div class="section page-section tech col-half" v-if="techMode && hasModel">
                <div class="title">Model assets</div>
                <item-part-assets :parts="itemData.parts"/>
            </div>

            <face-textures
                v-if="techMode && itemType == 'PARTS' && itemData.parts && itemData.parts.id"
                :partsId="itemData.parts.id"
                :region="region"
                :key="`face-${itemData.parts.id}`"/>

            <div class="section page-section"
                v-if="itemData.type.potentialId && itemData.potentials && (itemData.potentials.length > 1 || (itemData.potentials.length > 0 && isSkillOnly))">
                <div class="title">
                    Variations<span class="tech-text" v-if="techMode"> · {{ itemData.type.potentialId }}</span>
                </div>
                <item-potential
                    :potentialData="itemData.potentials"
                    :potentialNum="potentialNum"
                    :selection="potentialId"
                    v-on:potentialIdChange="onPotentialIdUpdate"
                ></item-potential>
            </div>

            <div class="section page-section" v-if="itemData.itemSet">
                <div class="title">Item Set</div>
                <item-set :setData="itemData.itemSet" :itemId="itemData.id" :jobs="itemData.needClass"></item-set>
            </div>

            <!-- {{ itemData.type }} -->

            <div class="section page-section" v-if="itemType == 'ENHANCEMENT_HAMMER'">
                <div class="title">Enhancement Hammer</div>
                <mobile-enhance :mobileEnchantId="itemData.type.mobileEnchantId"></mobile-enhance>
            </div>

            <div class="section page-section" v-if="itemType == 'ITEM_TUNER'">
                <div class="title">Item Tuner</div>
                <item-tuner :tunerId="itemData.type.changeMatchingId" :parentItemId="itemId"></item-tuner>
            </div>

            <div class="section page-section col-half" v-if="containerItem">
                <div class="title">Box Contents</div>
                <item-charm
                    :charmId="itemData.type.charmNum || itemData.type.param1"
                    :itemType="itemData.type"
                    :itemId="itemId"
                />
            </div>
            <div class="section page-section col-half" v-if="itemType == 'RANDOM'">
                <div class="title">Box Contents</div>
                <item-random
                    :randomId="itemData.type.itemDropTableId || itemData.type.param1"
                    :itemType="itemData.type"
                >
                </item-random>
            </div>

            <div class="section page-section" v-if="itemData.enchantId">
                <div class="title">Enhancement details<span class="tech-text" v-if="techMode"> · enchant {{ itemData.enchantId }}</span></div>
                <item-enhance
                    :itemData="itemData"
                    :enhanceData="enhanceData"
                    :loading="enhanceLoading"
                    :error="enhanceError"
                    :level="enhanceLevel"
                    :useGoldenGoose="useGoldenGoose"
                    :useJelly="useJelly"
                    :dragonGem="dragonGem"
                    v-on:levelUpdate="onLevelUpdate"
                    v-on:retry="fetchEnhanceData"/>
            </div>
            <div class="section page-section col-half" v-if="itemData.extract && Object.keys(itemData.extract.results).length">
                <div class="title">Extraction</div>
                Extracting 
                <span v-if="itemData.enchantId">at +{{enhanceLevel}}</span> 
                costs <b>{{ itemData.extract.cost | gold }}</b> and can give one of:

                <transition-group name="fade-item" tag="div" class="item-list" v-if="itemData.extract.results[effectiveEnhanceLevel] && !enhanceData[effectiveEnhanceLevel - 1]">
                    <div class="entry" v-for="(iid, key) in itemData.extract.results[effectiveEnhanceLevel]" :key="key">
                        <item-card :itemId="iid"></item-card>
                    </div>
                </transition-group>
                <transition name="fade-item" v-else-if="enhanceData[effectiveEnhanceLevel - 1]">
                    <item-drop-list :dropId="enhanceData[effectiveEnhanceLevel - 1].disjointDropId" />
                </transition>

                <div class="info toast"
                    v-if="!$route.query.embed">
                    <div class="icon">
                        <i class="fa fa-exclamation-triangle"></i>
                    </div>
                    <div class="content">
                        <div class="heading">
                            Darkness Falls
                        </div>
                        <p>
                            Sorry! We can't show extraction rates because they aren't in the client.
                        </p>
                    </div>
                </div>
            </div>

            <div class="section page-section col-half"
                v-if="!$route.query.embed">
                <div class="title">Procurement</div>
                <!-- <div v-if="itemData.gainText" class="game-tooltip">
                    <div class="title">Game Tooltip</div>
                    <div v-html="itemData.gainText" class="gain uistring"></div>
                </div> -->
                <item-acquire
                    :item="itemData"
                    >
                </item-acquire>

            </div>

            <item-tunings :itemId="itemId" :parentItem="itemData"></item-tunings>
            </div><!-- /item-body -->
        </div>
    </div>
</template>

<script>
import Vue from 'vue';
import ItemIcon from "@/old/game/ItemIcon";
import Points from "@/old/game/Points";
import StatGrid from "@/old/game/StatGrid";
import ItemCard from "@/components/item/DvItemCard.vue";
import ItemEnhance from "@/old/items/ItemEnhance";
import ItemTuner from "@/old/items/ItemTuner";
import ItemCharm from "@/old/items/ItemCharm";
import ItemRandom from "@/old/items/ItemRandom";
import MobileEnhance from "@/old/items/MobileEnhance";
import ItemPotential from "@/old/items/ItemPotential";
import ItemAcquire from "@/old/items/ItemAcquire";
import ItemSet from "@/old/items/ItemSet";
import ItemTunings from "@/old/items/ItemTunings";
import ItemParts from "@/old/items/ItemParts";
import ItemPartAssets from "@/old/items/ItemPartAssets.vue";
import FaceTextures from "@/components/item/FaceTextures.vue";
import BigErrorBox from '@/old/util/BigErrorBox';
import ItemDropList from "@/components/item/ItemDropList.vue";
import ItemAttribs from "@/old/items/ItemAttribs";

import Item from "@/old/api/item/item";
import ItemStat from "@/old/api/item/itemstat";

import CopyLink from "@/components/util/CopyLink.vue";
import RankSlider from "@/components/util/RankSlider.vue";
import { ApiHttpClient } from "@/globals";

import SkillStubLink from "@/components/skill/SkillStubLink.vue";
import UiString from "@/components/uistring/UiString.vue";
import TitleInfo from "@/components/title/TitleInfo.vue";
import ItemCustomColor from "@/components/item/ItemCustomColor.vue";

import FavoritesProvider from "@/api/FavoritesProvider";
import { ensureRegion } from '@/api/RegionProvider';

import {
    maxEnhanceLevel as calcMaxEnhanceLevel,
    clampEnhanceLevel,
    canUseJelly as calcCanUseJelly,
    enhanceStatSetFor,
    breakThreshold as calcBreakThreshold,
} from "@/util/ItemEnhanceMath";

Vue.component('item-icon', ItemIcon);
Vue.component('point-tag', Points);
Vue.component('stat-grid', StatGrid);
Vue.component('item-card', ItemCard);
Vue.component('item-enhance', ItemEnhance);
Vue.component('item-tuner', ItemTuner);
Vue.component('item-charm', ItemCharm);
Vue.component('item-random', ItemRandom);
Vue.component('item-potential', ItemPotential);
Vue.component('item-set', ItemSet);
Vue.component('item-tunings', ItemTunings);
Vue.component('mobile-enhance', MobileEnhance);
Vue.component('item-acquire', ItemAcquire);
Vue.component('item-parts', ItemParts);
Vue.component('item-drop-list', ItemDropList);
Vue.component('item-attribs', ItemAttribs);

Vue.component('big-error-box', BigErrorBox);

export default {
    name: "item-page",
    components: {
        SkillStubLink,
        CopyLink,
        ItemCustomColor,
        UiString,
        TitleInfo,
        RankSlider,
        ItemPartAssets,
        FaceTextures,
    },
    data: function() {
        return {
            itemId: this.extractItemId(this.$route.params.itemId),
            loading: true,
            itemData: null,
            enhanceLevel: 0,
            enhanceData: [],
            enhanceLoading: false,
            enhanceError: null,
            useGoldenGoose: true,
            useJelly: true,
            potentialNum: 0,
            potentialId: 0,
            error: null,
            copyStatus: null,
            favorite: false,
        }
    },
    created() {
        this.fetchData();
    },
    watch: {
        '$route'(to, from) {
            this.itemId = this.extractItemId(this.$route.params.itemId);
        },
        itemId(to, from) {
            if (to != from) {
                this.fetchData();
            }
        },
        enhanceLevel(to, from) {
            if (to != from) {
                this.updateQueryParams();
            }
        },
        potentialNum(to, from) {
            if (to != from) {
                this.updateQueryParams();
            }
        },
        potentialId(to, from) {
            if (to != from) {
                this.updateQueryParams();
            }
        },
        region(to, from) {
            if (to != from) {
                this.fetchEnhanceData();
            }
        },
    },
    computed: {
        region() {
            return this.$store.state.regionCode;
        },
        baseUrl() {
            return ApiHttpClient.defaults.baseURL;
        },
        name() {
            if (this.itemData.name) {
                if (this.itemData.name.name && this.itemData.name._NameID) {
                    return this.itemData.name.name;
                } else if (this.itemData.name._NameID) {
                    return this.techMode
                        ? "Item MID:" + this.itemData.name._NameID
                        : "Unnamed Item " + this.itemId;
                }
            }
            
            return "Unnamed Item " + this.itemId;
        },
        nameMessage() {
            return {
                id: Number((this.itemData.name && this.itemData.name._NameID) || 0),
                message: this.name,
            };
        },
        ownerTable() {
            if (this.itemData) {
                return this.itemData.ownerTable;
            }

            return 'virt.itemtable';
        },
        canUse() {
            if (this.itemData.needClass) {
                return this.itemData.needClass.filter(c => c.id != 0).map(c => c.displayName).join("/\u200B");
            }

            return null;
        },
        typeTechNote() {
            const typeId = this.itemData.type.typeId;
            const raw = this.itemData.type.type;
            //  The raw enum only tells the reader something when it differs from the
            //  friendly category — "Weapon WEAPON (0)" is noise, "Vehicle
            //  VEHICLE_MULTI_MOUNT (12)" is not.
            const norm = (s) => String(s || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
            if (norm(raw) === norm(this.category)) {
                return `(${typeId})`;
            }

            return `${raw} (${typeId})`;
        },
        canUseIds() {
            if (this.itemData.needClass) {
                return this.itemData.needClass.filter(c => c.id != 0).map(c => c.id).join(",");
            }

            return null;
        },
        category() {
            return Item.getItemDetailedCategory(this.itemData);
        },
        containerItem() {
            let t = this.itemData.type.type;
            let tid = this.itemData.type.typeId;
            return t === "ITEM_DROP_POUCH" || t === "MULTI_ITEM_DROP_POUCH" || t === "ITEM_DROP_DUAL_POUCH" ||
                t === "HERO_POUCH" || tid === 142;
        },
        forceBound() {
            //  Certain items (COUGH QUEST) are untradable at all times
            let t = this.itemData.type.type;
            return t === "QUEST";
        },
        hasModel() {
            return !!this.itemData.parts && this.itemData.parts.parts.length;
        },
        //  Only decal parts still render anything in the preview box; skn parts
        //  relied on the disabled 3D viewer.
        hasDecal() {
            return !!this.hasModel && this.itemData.parts.parts.some((p) => !!p.decalName);
        },
        statSet() {
            let ret = ItemStat.joinStatSet(this.itemData.stats);
            if (this.itemData.potentials) {
                let potentials = this.itemData.potentials;
                if (potentials.length == 1) {
                    let potSet = ItemStat.joinStatSet(potentials[0].states, "value");
                    ret = ItemStat.zipStatSets(ret, potSet);
                }
            }

            return ret;
        },
        effectiveEnhanceLevel() {
            if (isNaN(this.enhanceLevel)) {
                return 0;
            }

            return this.enhanceLevel;
        },
        itemType() {
            return this.itemData.type.type;
        },
        hasExtraData() {
            let t = this.itemData.type.type;
            switch(t) {
                case "HAIRDYE":
                case "ENCHANT_JELLY":
                case "REVIVE_SCROLL":
                case "SKINDYE":
                case "CONTACT_LENS":

                case "TWO_TONE_HAIRDYE":
                    return true;
            }

            return false;
        },
        combinedGearScore() {
            let ret = {
                base: this.itemData.gearScore,
                potential: this.potentialGearScore,
                enhance: this.enhanceGearScore
            };

            ret.total = ret.base + ret.potential + ret.enhance;

            return ret;
        },
        potentialGearScore() {
            if (this.potentialId) {
                for (let k in this.itemData.potentials) {
                    let v = this.itemData.potentials[k];
                    if (v.id == this.potentialId) {
                        return v.gearScore;
                    }
                }
            }

            return 0;
        },
        enhanceGearScore() {
            if (this.enhanceLevel && this.enhanceData && this.enhanceData[this.enhanceLevel - 1]) {
                return this.enhanceData[this.enhanceLevel - 1].gearScoreBonus;
            }

            return 0;
        },
        link() {
            let queries = {};
            queries = Object.assign(queries, this.$route.query);
            delete queries["embed"];
            queries.region = this.$store.state.regionCode;

            let url = window.location.origin + this.$route.path;

            let qString = Object.keys(queries).map(k => `${k}=${queries[k]}`).join("&");
            if (qString.length > 0) {
                url += "?" + qString;
            }

            return url;
        },
        isSkillOnly() {
            return this.itemData.potentials.filter((p) => p.skill).length == this.itemData.potentials.length;
        },
        maxEnhanceLevel() {
            return calcMaxEnhanceLevel(this.enhanceData);
        },
        canUseJelly() {
            return calcCanUseJelly(this.enhanceData);
        },
        enhanceLevelStats() {
            return enhanceStatSetFor(this.enhanceData, this.enhanceLevel);
        },
        hasEnhance() {
            return !!(this.itemData && this.itemData.enchantId);
        },
        dragonGem() {
            return !!this.itemData && this.itemData.type.type === 'DRAGON_GEM';
        },
        techMode() {
            return this.$store.state.techMode;
        },
        breakThreshold() {
            return calcBreakThreshold(this.enhanceData, this.useJelly);
        },
    },
    methods: {
        fetchData() {
            this.loading = true;
            this.error = null;
            this.enhanceLevel = Number(this.$route.query.enhance);
            this.enhanceData = [];
            this.enhanceError = null;
            this.enhanceLoading = false;
            this.potentialNum = Number(this.$route.query.potential);
            this.potentialId = Number(this.$route.query.p);
            if (isNaN(this.enhanceLevel)) {
                this.enhanceLevel = 0;
            }

            this.favorite = false;
            FavoritesProvider.getFavorites('item').then((favorites) => {
                this.favorite = new Set(favorites).has(String(this.itemId));
            });

            this.itemData = null;
            Item.getItem(this.itemId, this.region,
                (res) => {
                    this.itemData = res;
                    this.loading = false;
                    this.fetchEnhanceData();
                    this.updateQueryParams();
                },
                (err) => {
                    this.loading = false;
                    if (err.response) {
                        if (err.response.status == 404) {
                            this.error = {
                                statusText: "Item not found",
                                bodyText: "The item you are looking for does not exist in this region. Double check the ID or try another region."
                            }
                        } else if (err.response.status >= 500) {
                            this.error = {
                                statusText: "Service error",
                                bodyText: "There was an issue fetching data for this item. Please try again later.",
                            }
                        } else {
                            this.error = {
                                statusText: "Unknown error",
                                bodyText: axiosErrorToString(err),
                            }
                        }
                    } else {
                        this.error = err;
                    }
                });
        },
        fetchEnhanceData() {
            const enchantId = this.itemData && this.itemData.enchantId;
            this.enhanceData = [];
            this.enhanceError = null;

            if (!enchantId) {
                this.enhanceLoading = false;
                return;
            }

            this.enhanceLoading = true;
            Item.getEnhancementInfo(enchantId, this.region,
                (res) => {
                    this.enhanceData = res;
                    this.enhanceLoading = false;
                    this.enhanceError = null;
                    //  A deep-linked ?enhance= may exceed this item's real max.
                    this.enhanceLevel = clampEnhanceLevel(this.enhanceLevel, this.maxEnhanceLevel);
                    //  Dragon gems default to no jelly; other items default to using one.
                    this.useJelly = !this.dragonGem;
                    //  Reset per item: Item.vue survives itemId changes, so data()'s
                    //  initializer does not re-run on in-app navigation.
                    this.useGoldenGoose = true;
                    if (this.$fOn(this.$f.DisableGoldenGoose)) {
                        this.useGoldenGoose = false;
                    }
                },
                (err) => {
                    this.enhanceLoading = false;
                    this.enhanceError = err;
                });
        },
        onLevelUpdate(newLevel) {
            this.enhanceLevel = clampEnhanceLevel(newLevel, this.maxEnhanceLevel);
        },
        onEnhanceStep({ dir, shift }) {
            const target = shift
                ? (dir < 0 ? 0 : this.maxEnhanceLevel)
                : this.enhanceLevel + dir;
            this.onLevelUpdate(target);
        },
        onPotentialIdUpdate(newPotentialId, index) {
            this.potentialId = newPotentialId;
            this.potentialNum = index;
        },
        updateQueryParams() {
            let query = {};
            if (this.enhanceLevel > 0) {
                query.enhance = this.enhanceLevel;
            }

            if (this.potentialId > 0) {
                query.p = this.potentialId;
            }
            
            if (this.potentialNum > 0) {
                query.potential = this.potentialNum;
            }

            if (this.$route.query.embed) {
                query.embed = this.$route.query.embed;
            }

            this.$router.replace({ 
                name: 'item-view', 
                params: {
                    itemId: this.toEasyName(this.itemId, this.itemData)
                }, 
                query: query
            });
        },
        extractItemId(str) {
            let split = str.split("-", 2);
            return Number(split[0]);
        },
        toEasyName(itemId, itemData) {
            return Item.itemEasyUrl(itemId, itemData);
        },
        goBack() {
            this.$router.back();
        },
        copyLink() {
            let box = this.$refs['copyLink'];
            box.value = this.link;
            box.focus();
            box.select();
            let success = document.execCommand('copy');
            if (success) {
                this.copyStatus = 'ok';
            } else {
                this.copyStatus = 'err';
            }

            setTimeout(() => this.copyStatus = null, 2000);
        },
        toggleFavorite() {
            if (!this.favorite) {
                FavoritesProvider.addFavorite('item', String(this.itemId));
            } else {
                FavoritesProvider.removeFavorite('item', String(this.itemId));
            }
            
            this.favorite = !this.favorite;
        },
        ddsPngUrl(name) {
            const region = ensureRegion(undefined, true);
            return `${this.baseUrl}/server/${region}/dds/${name}/png`;
        }
    }
}
</script>

<style lang="less" scoped>
@import "../../less/core.less";
@import "../../less/common/variables.less";
@import "../../less/common/mixins.less";

.item-summary {
    position: sticky;
    top: 0;
    z-index: 5;
    background: @dv-c-background;
    padding-bottom: 8px;
}

.itempage {
    position: relative;
    min-height: 1vh;
    margin-top: 0;
    padding-top: 0;
    width: 100%;
    .page-header > .title > .chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        margin-top: 4px;
    }

    .page-header {
        .favorite {
            position: relative;
            text-align: right;

            .fav-star {
                cursor: pointer;
                font-size: 36px;
                transition: color 0.125s ease-in;
                &.favorited,
                &:hover {
                    color: #ada12d;
                }
            }

            .helper {
                position: absolute;
                color: transparent;
                white-space: nowrap;
                right: 44px;
                top: 8px;
                transition: color 0.125s ease-in;
            }

            &:hover .helper {
                color: @dv-c-accent-2;
            }
        }
    }

    .stats-panel {
        min-height: 186px;
        box-sizing: border-box;
        padding: 12px 14px;
        background: @dv-tile-well-bg-color;
        border: 1px solid @dv-tile-well-border-color;
        border-radius: @dv-tile-border-radius-m;

        .enhance-control {
            margin-bottom: 12px;

            .enhance-opts {
                display: flex;
                gap: 16px;
                margin-bottom: 8px;
            }
            .rank-caption {
                text-align: center;
                margin-top: 6px;
                color: @dv-c-idle;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.1em;

                > b {
                    color: @dv-c-foreground;
                    font-size: 14px;
                }

                .breaks { color: @dv-c-red; }
            }
        }
    }

    .description-panel {
        box-sizing: border-box;
        padding: 12px 14px;
        background: @dv-tile-well-bg-color;
        border: 1px solid @dv-tile-well-border-color;
        border-radius: @dv-tile-border-radius-m;

        .desc {
            position: relative;
            margin-top: 25px;
            border-top: 1px solid @dv-c-accent-1;
            padding-top: 4px;
            &::before {
                content: "Description";
                font-size: 12px;
                color: @dv-c-accent-1;
                text-transform: uppercase;
                letter-spacing: 0.2em;
                position: absolute;
                top: -1.3em;
                .left(0);
            }

            .gain {
                margin-top: 0.5em;
            }

            .skill {
                margin-top: 16px;
            }
        }

        .extra-data {
            margin-top: 1em;
        }
    }

    .item-body {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        align-items: start;

        @media (max-width: 640px) {
            grid-template-columns: 1fr;
        }

        // wide by default: sections span both columns unless opted into a single column
        // (longhand: this project's Less compiler evaluates the bare slash in
        //  `grid-column: 1 / -1` as division -> -1, which voids the span)
        > .page-section,
        > .stat-desc-row {
            grid-column-start: 1;
            grid-column-end: -1;
        }

        // narrow sections that pair up
        > .page-section.col-half { grid-column: auto; }
    }

    .stat-desc-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        align-items: start;
        margin: 8px 0;

        .stats-panel { min-width: 0; }
        .description-panel { min-width: 0; }

        &.no-stats {
            grid-template-columns: 1fr;
        }

        @media (max-width: 640px) {
            grid-template-columns: 1fr;
        }
    }

    .model-section {
        //  ItemParts.vue is 100%/100% with absolutely-positioned children,
        //  so it contributes no content height; without an explicit height
        //  here (matching the pre-redesign @3d-size: 256px) this well
        //  collapses to a sliver.
        height: 256px;
        background: @dv-tile-well-bg-color;
        border: 1px solid @dv-tile-well-border-color;
        border-radius: @dv-tile-border-radius-m;
    }

    .gems {
        margin-top: 12px;
        border-top: 1px solid fade(@dv-c-accent-1, 25%);
        padding-top: 6px;
        display: flex;
        flex-direction: row;

        .gemslot {
            flex: 0 0 48px;
            width: 48px;
            height: 48px;
            position: relative;
            margin: 0 2px;

            .count {
                position: absolute;
                .right(0px);
                bottom: 0px;
            }
        }
    }

    .item-list {
        margin-top: 10px;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;

        .entry {
            flex: 1 1 400px;
            border: 1px solid @dv-c-foreground;
            border-top-color: transparent;

            &:first-child {
                border-top-color: @dv-c-foreground;
            }

            @media only screen and (min-width:@min-desktop-wide-width) {
                flex: 0 1 460px;
                .border-right-color(transparent);
                &:first-child,
                &:nth-child(2) {
                    border-top-color: @dv-c-foreground;
                }
                &:nth-child(2n) {
                    .border-right-color(@dv-c-foreground);
                }
                &:last-child {
                    .border-right-color(@dv-c-foreground);
                    padding-right: 1px;
                }
                &:first-child:last-child {
                    .border-right-color(@dv-c-foreground);
                }

            }
        }

    }
     
    .game-tooltip {
        position: relative;
        margin-top: 2em;
    }

    
    .loading {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;

        // padding-top: 100px;
        .loader-box {
            position: absolute;
            top: 150px;
            left: 0;
            right: 0;
            bottom: 0;
            padding: 16px 0;;
            text-align: center;
            
            .label {
                position: absolute;
                left: 0;
                right: 0;
                top: 50%;
                text-transform: uppercase;
                letter-spacing: 0.2em;
                color: @dv-c-foreground;
                transform: translate(0, 4px);
            }

            .loader,
            .loader:after {
                border-radius: 50%;
                width: 10em;
                height: 10em;
            }
            .loader {
                position: absolute;
                left: 50%;
                top: 50%;
                line-height: 100%;
                font-size: 16px;
                margin: -5em -6em;
                text-indent: -9999em;
                border-top: 1.1em solid rgba(0, 0, 0, 0.75);
                .border-right(1.1em solid rgba(0, 0, 0, 0.75));
                border-bottom: 1.1em solid rgba(0, 0, 0, 0.75);
                .border-left(1.1em solid @dv-c-foreground);
                background: rgba(0, 0, 0, 0.5);
                //   -webkit-transform: translateZ(0);
                //   -ms-transform: translateZ(0);
                //   transform: translateZ(0);
                -webkit-animation: load8 1.1s infinite linear;
                animation: load8 1.1s infinite linear;
            }
        }
    }
}
</style>
